import React, { useEffect, useState } from "react";
import { Plus, GripVertical, X } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ContentSectionEditor from "./components/ContentSectionEditor";
import { useFormikContext } from "formik";

const SortableSection = ({ section, onUpdate, onDelete, read }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 0,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group transition-all ${
        isDragging ? "shadow-2xl z-50" : ""
      }`}
    >
      {!read && (
        <>
          {/* Mobile Drag/Delete Bar (< lg) */}
          <div className="flex lg:hidden items-center justify-between bg-slate-100/80 border border-slate-200/80 rounded-t-xl px-3 py-1.5 mb-1">
            <div
              {...attributes}
              {...listeners}
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-grab active:cursor-grabbing py-1"
            >
              <GripVertical className="w-4 h-4 text-slate-400" />
              <span>Drag Section</span>
            </div>
            <button
              type="button"
              onClick={onDelete}
              className="p-1 text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
              title="Delete Section"
            >
              <Plus className="w-4 h-4 rotate-45" />
            </button>
          </div>

          {/* Desktop Floating Actions Handle (≥ lg) */}
          <div className="hidden lg:flex absolute -left-12 top-2 flex-col gap-2 items-center z-10">
            <div
              {...attributes}
              {...listeners}
              className="p-2 text-slate-400 hover:text-primary bg-white shadow-xs border border-slate-200/80 rounded-xl cursor-grab active:cursor-grabbing transition-all hover:scale-105"
              title="Drag Section"
            >
              <GripVertical className="w-4 h-4" />
            </div>
            <button
              type="button"
              onClick={onDelete}
              className="p-2 text-slate-400 hover:text-rose-500 bg-white shadow-xs border border-slate-200/80 rounded-xl opacity-0 group-hover:opacity-100 transition-all hover:scale-105"
              title="Delete Section"
            >
              <Plus className="w-4 h-4 rotate-45" />
            </button>
          </div>
        </>
      )}

      <ContentSectionEditor section={section} onUpdate={onUpdate} read={read} />
    </div>
  );
};

const Editor = ({ name, read = false }) => {
  const { values, setFieldValue } = useFormikContext();
  const contentData = values?.[name];

  // Initialize state once from Formik value if present
  const [sections, setSections] = useState(() => {
    if (Array.isArray(contentData) && contentData.length > 0) {
      return contentData;
    }
    return [
      {
        id: crypto.randomUUID(),
        title: "Introduction",
        headingTag: "h2",
        blocks: [
          {
            id: crypto.randomUUID(),
            type: "rich-text",
            content: "<p>Start editing this block.</p>",
          },
        ],
      },
    ];
  });

  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Sync internal section state back to Formik safely
  useEffect(() => {
    if (JSON.stringify(contentData) !== JSON.stringify(sections)) {
      setFieldValue(name, sections);
    }
  }, [sections, name, setFieldValue]);

  const addSection = () => {
    setSections((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        title: "",
        headingTag: "h3",
        blocks: [],
      },
    ]);
  };

  const updateSection = (id, updates) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...updates } : s)),
    );
  };

  const deleteSection = (id) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleImport = () => {
    try {
      const parsed = JSON.parse(importText);
      if (Array.isArray(parsed)) {
        setSections(parsed);
        setShowImportModal(false);
        setImportText("");
      } else {
        alert("JSON must be an array of sections.");
      }
    } catch (e) {
      alert("Invalid JSON format. Please check your input.");
    }
  };

  return (
    <div className="w-full min-h-screen bg-slate-50 text-slate-900 rounded-xl overflow-hidden border border-slate-200/80">
      <main className="w-full">
        {/* Header Bar */}
        <header className="bg-white border-b border-slate-200/80 px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 sm:gap-4 min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-slate-900 truncate tracking-tight">
              Content Engine
            </h1>
            <span
              className={`shrink-0 px-2.5 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wider border ${
                read
                  ? "bg-slate-100 text-slate-600 border-slate-300"
                  : "bg-indigo-50 text-primary border-indigo-200"
              }`}
            >
              {read ? "Read Only" : "Editor"}
            </span>
          </div>
        </header>

        {/* Content Area */}
        <div className="w-full max-w-7xl mx-auto py-6 sm:py-10 px-3 sm:px-6 lg:px-12">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sections.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-6 sm:space-y-10">
                {sections.map((section) => (
                  <SortableSection
                    key={section.id}
                    section={section}
                    read={read}
                    onUpdate={(upd) => updateSection(section.id, upd)}
                    onDelete={() => deleteSection(section.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {/* Add Section Action */}
          {!read && (
            <div className="flex justify-center w-full mt-8 sm:mt-12 px-2">
              <button
                type="button"
                onClick={addSection}
                className="w-full sm:max-w-md inline-flex items-center justify-center gap-2 px-5 py-3 bg-indigo-50 hover:bg-indigo-100 text-primary border border-indigo-200 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-150 active:scale-95 shadow-xs cursor-pointer"
              >
                <Plus className="w-4 h-4 shrink-0" />
                <span>Add Section</span>
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Responsive Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-3 sm:p-4">
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-xs"
            onClick={() => setShowImportModal(false)}
          />
          <div className="relative bg-white w-full max-w-xl max-h-[90vh] rounded-2xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="p-4 sm:p-6 border-b border-slate-100 flex items-center justify-between shrink-0">
              <h2 className="text-base sm:text-lg font-bold text-slate-900">
                Import Content State
              </h2>
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto">
              <textarea
                className="w-full h-48 sm:h-64 bg-slate-50 border border-slate-200 rounded-xl p-3 sm:p-4 font-mono text-xs sm:text-sm text-slate-800 focus:ring-2 focus:ring-indigo-500/20 focus:border-primary focus:outline-hidden transition-all"
                placeholder="Paste your JSON here..."
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
              />
            </div>
            <div className="p-4 sm:p-6 border-t border-slate-100 flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3 sm:justify-end shrink-0">
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                className="w-full sm:w-auto px-5 py-2.5 text-xs sm:text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleImport}
                className="w-full sm:w-auto bg-primary hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl text-xs sm:text-sm font-semibold shadow-xs transition-all active:scale-95"
              >
                Hydrate State
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Editor;
